import { type ComponentProps } from 'react';
import { type OptimizelyDecision } from '@optimizely/react-sdk';
import { HeroBanner, PerksList } from '@fff-web/fff-ui-shared';

export type IProductsList = Array<{
  imagePublicId: string;
  title: string;
  caption: string;
}>;

type HeroBannerOptimizely = ComponentProps<typeof HeroBanner> & {
  imageDesktop: string;
  imageMobile: string;
};

export type ReactivateMembershipVariablesType = {
  planSectionSubtitle: string;
  planSectionTitle: string;
  seasonalPerks: {
    list: PerksList;
  };
  annualPerks: {
    list: PerksList;
  };
  heroBanner: HeroBannerOptimizely;
  howItWorks: {
    headline: string;
    productsList: IProductsList;
  };
  seasonMembershipName: string;
  increasedPrice: number;
  annualCardSubtitle: string;
};

export interface IReactivateMembershipContext extends OptimizelyDecision {
  variables: ReactivateMembershipVariablesType;
}

export type MyMembershipContextType = {
  growthMyMembership: OptimizelyDecision;
};

export const initialStateDecision: OptimizelyDecision = {
  variationKey: null,
  userContext: {
    id: null,
    attributes: undefined,
  },
  enabled: false,
  variables: {},
  ruleKey: null,
  flagKey: '',
  reasons: [],
};

export const REACTIVATE_MEMBERSHIP_EVERGREEN_CONTENT: ReactivateMembershipVariablesType = {
  annualCardSubtitle: 'Prepay 4 boxes and save $20',
  annualPerks: {
    list: [
      {
        id: 1,
        perk: 'Seasonal Box with 6 full-size products',
        included: true,
        tooltip: '',
      },
      {
        id: 2,
        perk: 'Customize your entire box',
        included: true,
        tooltip: 'Each season you can pick all products that go in your box.',
      },
      {
        id: 3,
        perk: 'Unlimited Swap for Credit',
        included: true,
        tooltip:
          'Credit can be applied toward e-commerce purchases (excludes membership fees and gift cards).',
      },
      {
        id: 4,
        perk: 'Early access to Customize',
        included: true,
        tooltip: '',
      },
      {
        id: 5,
        perk: 'Early access to Add-Ons/Edit Sales',
        included: true,
        tooltip: '',
      },
      {
        id: 6,
        perk: 'Earlier shipping',
        included: true,
        tooltip: '',
      },
      {
        id: 7,
        perk: 'Cancel anytime',
        included: true,
        tooltip: '',
      },
    ],
  },
  heroBanner: {
    headline: {
      text: 'Fully Customize Your Fall Box!',
      color: '#FFF',
    },
    caption: {
      text: 'Join Over 1 Million Members Saving $1,702* a Year',
      color: '#FFF',
    },
    disclaimer: {
      text: 'Average savings vs. MSRP, based on annual members who shopped at least one additional sale in 2022.',
      color: '#FFF',
    },
    primaryCTA: {
      label: 'GET STARTED',
      included: true,
    },
    imageDesktop: 'https://scontent01.fabfitfun.com/gtb/cdn/FALL-GIFT200-2200x990.jpg',
    imageMobile: 'https://scontent01.fabfitfun.com/gtb/cdn/FALL-GIFT200-375x710.jpg',
  },
  howItWorks: {
    headline: '',
    productsList: [
      {
        imagePublicId: 'https://scontent01.fabfitfun.com/pandora/cdn/openbox.png',
        title: 'Customizable',
        caption: 'Each season, we curate a box of the hottest deals. You make it 100% you.',
      },
      {
        imagePublicId: 'https://scontent01.fabfitfun.com/pandora/cdn/swap.png',
        title: 'Stay in Control',
        caption: 'Not in love with the season’s choices? You can always swap for credit to shop.',
      },
      {
        imagePublicId: 'https://scontent01.fabfitfun.com/pandora/cdn/fullsize.png',
        title: 'Shop Anytime',
        caption: 'Shop and save with our shop and curated weekly sales.',
      },
    ],
  },
  increasedPrice: 0,
  planSectionSubtitle:
    'See why over 1 million members love us! Select a plan and reactivate below.',
  planSectionTitle: 'Choose a Plan',
  seasonalPerks: {
    list: [
      {
        id: 1,
        perk: 'Seasonal Box: 6 full-size products',
        included: true,
        tooltip: '',
      },
      {
        id: 2,
        perk: '4 Customizations',
        included: true,
        tooltip: 'Each season you can pick 4 of the products that go in your box.',
      },
      {
        id: 3,
        perk: 'Unlimited Swap for Credit',
        included: true,
        tooltip:
          'Credit can be applied toward e-commerce purchases (excludes membership fees and gift cards).',
      },
      {
        id: 4,
        perk: 'Early access to Customize',
        included: false,
        tooltip: '',
      },
      {
        id: 5,
        perk: 'Early access to Add-Ons/Edit Sales',
        included: false,
        tooltip: '',
      },
      {
        id: 6,
        perk: 'Earlier shipping',
        included: false,
        tooltip: '',
      },
      {
        id: 7,
        perk: 'Cancel anytime',
        included: true,
        tooltip: '',
      },
    ],
  },
  seasonMembershipName: 'Fall Box',
};

const defaultReactivateMyMembershipContext: MyMembershipContextType = {
  growthMyMembership: initialStateDecision,
};

export default defaultReactivateMyMembershipContext;
